/**
 * StatsGrid — Responsive 2x2 (mobile) / 4-column (desktop) grid of token stats.
 *
 * Displays: Price, Market Cap, 24h Volume, Holders.
 * Includes DataFreshness indicator and stale data treatment.
 *
 * Story 4.2 — Task 3
 */

import type { TokenDetailStats } from "@repo/shared-types";

import { cn } from "../../lib/cn";
import {
	formatCompact,
	formatCount,
	formatPrice,
	formatPriceChange,
} from "../../lib/format-number";

type StatsGridProps = {
	stats: TokenDetailStats | null;
};

type StatCardProps = {
	label: string;
	value: string;
	subValue?: string | undefined;
	subClassName?: string | undefined;
	stale: boolean;
};

function StatCard({ label, value, subValue, subClassName, stale }: StatCardProps) {
	return (
		<div className="rounded-xl border border-pastel-border bg-white p-4">
			<p className="text-xs font-medium text-pastel-gray uppercase tracking-wide">{label}</p>
			<p className={cn("mt-1 text-lg font-bold text-pastel-black", stale && "opacity-60")}>
				{value}
			</p>
			{subValue && <p className={cn("text-xs font-medium mt-0.5", subClassName)}>{subValue}</p>}
		</div>
	);
}

function DataSourceBadge({
	dataSource,
	dataAge,
	stale,
}: { dataSource: string; dataAge: number; stale: boolean }) {
	const isWarning = stale || dataAge > 30;

	return (
		<div className="flex items-center gap-2 text-xs text-pastel-gray">
			<span
				className={cn(
					"h-2 w-2 rounded-full",
					isWarning ? "bg-yellow-400 animate-pulse" : "bg-pastel-vibrant-green"
				)}
			/>
			<span>
				{isWarning
					? `Updated ${String(dataAge)}s ago`
					: dataSource === "indexer"
						? "Live data"
						: dataSource === "cache"
							? "Cached"
							: ""}
			</span>
			{dataSource === "seeded" && (
				<span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-700">
					Sample Data
				</span>
			)}
		</div>
	);
}

export function StatsGrid({ stats }: StatsGridProps) {
	if (!stats) {
		return <StatsGridSkeleton />;
	}

	const priceChange = formatPriceChange(stats.priceChange24h);
	const stale = stats.stale || stats.dataAge > 30;

	return (
		<div>
			<div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
				<StatCard
					label="Price"
					value={formatPrice(stats.price)}
					subValue={priceChange.text}
					subClassName={priceChange.className}
					stale={stale}
				/>
				<StatCard label="Market Cap" value={formatCompact(stats.marketCap)} stale={stale} />
				<StatCard label="24h Volume" value={formatCompact(stats.volume24h)} stale={stale} />
				<StatCard label="Holders" value={formatCount(stats.holderCount)} stale={stale} />
			</div>
			<div className="mt-3">
				<DataSourceBadge dataSource={stats.dataSource} dataAge={stats.dataAge} stale={stale} />
			</div>
		</div>
	);
}

export function StatsGridSkeleton() {
	return (
		<div className="grid grid-cols-2 lg:grid-cols-4 gap-3 animate-pulse">
			{Array.from({ length: 4 }).map((_, i) => (
				<div
					key={`stat-skeleton-${String(i)}`}
					className="rounded-xl border border-pastel-border bg-white p-4"
				>
					<div className="h-3 w-16 bg-pastel-light-gray rounded mb-2" />
					<div className="h-6 w-24 bg-pastel-light-gray rounded" />
				</div>
			))}
		</div>
	);
}
